import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { MapPin, Clock, ChevronDown, ExternalLink, Menu, X, Instagram, ArrowRight, Upload, Send } from "lucide-react";
import AdminPage from "@/AdminPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSETS = {
  logo: "https://customer-assets-0z36b82j.emergentagent.net/job_8762bee0-d8d6-406d-9700-83541d17b85f/artifacts/w5oikw36_logo%20%284%29.png",
  chefTable: "https://customer-assets-0z36b82j.emergentagent.net/job_8762bee0-d8d6-406d-9700-83541d17b85f/artifacts/1olkrqwz_flat%20layout.png",
  ramen: "https://customer-assets-0z36b82j.emergentagent.net/job_8762bee0-d8d6-406d-9700-83541d17b85f/artifacts/wcct7fhr_ChatGPT%20Image%20Aug%2024%2C%202026%2C%2005_55_26%20PM.png",
  award: "https://customer-assets-0z36b82j.emergentagent.net/job_8762bee0-d8d6-406d-9700-83541d17b85f/artifacts/5xt56i85_58.JPG%20%281%29.webp",
  team: "https://customer-assets-0z36b82j.emergentagent.net/job_8762bee0-d8d6-406d-9700-83541d17b85f/artifacts/arrxxeil_IMG_5966%20Copy.webp",
  cake: "https://customer-assets-0z36b82j.emergentagent.net/job_8762bee0-d8d6-406d-9700-83541d17b85f/artifacts/1s8zdvvy_Untitled%20design%20%281%29.png",
  founder: "https://customer-assets.emergentagent.com/job_roch-founding-team/artifacts/rjvh0tuh_myphoto.jpg",
};

const ENTITY_SHORT = { roch_ramen: "Ramen", roch_chefs_table: "Chef's Table", roch_cake_studio: "Cake Studio", central_team: "Central" };
const ENTITY_FULL = { roch_ramen: "ROCH Ramen", roch_chefs_table: "ROCH Chef's Table", roch_cake_studio: "ROCH Cake Studio", central_team: "Central Team" };

// ===== Scroll Reveal Hook =====
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

// ===== Animated Counter =====
function Counter({ end, suffix = "", duration = 1600 }) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useReveal();
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = Math.max(1, Math.floor(end / (duration / 16)));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [visible, end, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
}

// ===== Marquee =====
function Marquee({ items, speed = "40s", reverse = false }) {
  return (
    <div className="marquee-wrapper overflow-hidden" data-testid="marquee">
      <div className={`marquee-track ${reverse ? "marquee-reverse" : ""}`} style={{ animationDuration: speed }}>
        {[...items, ...items].map((t, i) => (
          <span key={i} className="marquee-item font-editorial text-[clamp(1.8rem,4vw,3.5rem)] font-bold text-[#E5E5E7] select-none whitespace-nowrap">
            {t} <span className="mx-4 sm:mx-8 text-[#D97706]">/</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ===== Navbar =====
function Navbar({ onApply }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const links = [
    { label: "Concepts", href: "#concepts" },
    { label: "Why ROCH", href: "#why-roch" },
    { label: "Open Roles", href: "#roles" },
    { label: "Our Story", href: "#story" },
  ];

  return (
    <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/80 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.06)]" : "bg-transparent"}`}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <a href="#"><img src={ASSETS.logo} alt="ROCH" className="h-12 sm:h-14" loading="lazy" /></a>
          <div className="hidden md:flex items-center gap-8">
            {links.map(l => <a key={l.href} href={l.href} className="text-[13px] font-medium text-[#6E6E73] hover:text-[#1D1D1F] transition-colors">{l.label}</a>)}
            <button onClick={onApply} data-testid="nav-apply-btn" className="px-5 py-2 bg-[#1D1D1F] text-white text-[13px] font-medium rounded-full hover:bg-[#333] transition-colors">Apply Now</button>
          </div>
          <button onClick={() => setOpen(!open)} className="md:hidden p-2" data-testid="mobile-menu-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-[#E5E5E7] px-5 py-4 space-y-1">
          {links.map(l => <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-[15px] font-medium text-[#1D1D1F] py-2.5">{l.label}</a>)}
          <button onClick={() => { onApply(); setOpen(false); }} className="w-full py-3 mt-2 bg-[#1D1D1F] text-white font-medium rounded-full text-[15px]">Apply Now</button>
        </div>
      )}
    </nav>
  );
}

// ===== Hero =====
function Hero({ onApply, onExplore }) {
  return (
    <section data-testid="hero-section" className="relative min-h-[80vh] flex items-center bg-white overflow-hidden">
      <div className="absolute top-16 right-[-40px] w-44 sm:w-64 lg:w-80 opacity-[0.08] pointer-events-none select-none">
        <img src={ASSETS.ramen} alt="" className="rounded-[28px] rotate-6" loading="lazy" />
      </div>
      <div className="absolute bottom-8 left-[-30px] w-36 sm:w-56 lg:w-72 opacity-[0.06] pointer-events-none select-none">
        <img src={ASSETS.chefTable} alt="" className="rounded-[28px] -rotate-3" loading="lazy" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-24 sm:pt-32 pb-12">
        <div className="max-w-2xl">
          <p className="text-[13px] font-medium tracking-[0.2em] text-[#86868B] uppercase mb-4 animate-fade-in-up">ROCH Careers · Hyderabad</p>
          <h1 className="font-editorial text-[clamp(2.8rem,8vw,5.5rem)] font-bold text-[#1D1D1F] leading-[0.92] mb-5 animate-fade-in-up" style={{ animationDelay: "0.08s" }}>
            Not a job.<br /><span className="italic">A launchpad.</span>
          </h1>
          <p className="text-[17px] sm:text-[19px] text-[#6E6E73] leading-relaxed mb-8 max-w-md animate-fade-in-up" style={{ animationDelay: "0.16s" }}>
            Join the team building three very different culinary experiences under one roof.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 animate-fade-in-up" style={{ animationDelay: "0.24s" }}>
            <button onClick={onExplore} data-testid="hero-explore-btn" className="group px-7 py-3.5 bg-[#1D1D1F] text-white font-medium rounded-full hover:bg-[#333] transition-all text-[15px] inline-flex items-center gap-2">Explore Open Roles <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></button>
            <button onClick={onApply} data-testid="hero-apply-btn" className="px-7 py-3.5 border border-[#D1D1D6] text-[#1D1D1F] font-medium rounded-full hover:bg-[#F5F5F7] transition-colors text-[15px]">Apply Now</button>
          </div>
          <p className="flex items-center gap-1.5 mt-6 text-[13px] text-[#86868B] animate-fade-in-up" style={{ animationDelay: "0.32s" }}><MapPin size={13} /> Kondapur, Hyderabad</p>
        </div>
      </div>
    </section>
  );
}

// ===== Numbers Strip =====
function NumbersStrip() {
  const stats = [
    { num: 3, suffix: "", label: "Concepts" },
    { num: 10, suffix: "", label: "Seat Chef's Table" },
    { num: 8, suffix: "", label: "Course Tasting Menu" },
    { num: 1, suffix: "", label: "Founding Team" },
  ];
  const [ref, visible] = useReveal();
  return (
    <section ref={ref} data-testid="numbers-strip" className="py-10 sm:py-14 bg-[#1D1D1F]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-4">
          {stats.map((s, i) => (
            <div key={i} className={`text-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{ transitionDelay: `${i * 120}ms` }}>
              <p className="font-editorial text-[clamp(2.5rem,5vw,4rem)] font-bold text-white leading-none">
                <Counter end={s.num} suffix={s.suffix} />
              </p>
              <p className="text-[13px] sm:text-[14px] text-[#86868B] mt-1 font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== Concept Card =====
function ConceptCard({ c, i }) {
  return (
    <div className="group rounded-2xl overflow-hidden border border-[#E5E5E7] bg-white transition-all duration-500 hover:shadow-lg hover:-translate-y-1 h-full" data-testid={`concept-card-${i}`}>
      <div className="aspect-[2/1] sm:aspect-[4/3] overflow-hidden bg-[#F5F5F7] relative">
        <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <p className="absolute bottom-4 left-5 text-white font-editorial text-xl font-bold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">{c.name}</p>
      </div>
      <div className="p-4 sm:p-7">
        <h3 className="font-editorial text-[22px] font-bold text-[#1D1D1F] mb-1">{c.name}</h3>
        {c.hours && <p className="flex items-center gap-1.5 text-[12px] font-medium text-[#D97706] mb-2"><Clock size={12} />{c.hours}</p>}
        <p className="text-[14px] text-[#6E6E73] leading-relaxed mb-4">{c.desc}</p>
        <div className="flex flex-wrap gap-2">
          {c.tags.map((t, j) => <span key={j} className="px-3 py-1 bg-[#F5F5F7] text-[12px] font-medium text-[#6E6E73] rounded-full group-hover:bg-[#1D1D1F] group-hover:text-white transition-colors duration-300">{t}</span>)}
        </div>
      </div>
    </div>
  );
}

// ===== Three Concepts =====
function Concepts() {
  const data = [
    { key: "ramen", name: "ROCH Ramen", img: ASSETS.ramen, hours: "8 AM – 6 PM", desc: "A daytime Japanese ramen restaurant built around slow-cooked broths, Japanese technique and bold Indian character.", tags: ["Japanese-inspired kitchen", "Long-cooked ramen broths", "Fast-paced lunch service"] },
    { key: "chefs", name: "ROCH Chef's Table", img: ASSETS.chefTable, hours: "12 PM – 10 PM", desc: "A 10-seat progressive Telugu tasting menu experience built around technique, research and storytelling.", tags: ["10-seat intimate restaurant", "8-course tasting menu", "R&D-driven development"] },
    { key: "cake", name: "ROCH Cake Studio", img: ASSETS.cake, hours: "11 AM – 9 PM", desc: "A premium custom cake studio specialising in handcrafted buttercream cakes and large-format celebration pieces.", tags: ["Premium buttercream cakes", "Bespoke custom designs", "Luxury positioning"] },
  ];
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);
  useEffect(() => { checkScroll(); }, [checkScroll]);
  const scroll = (dir) => { scrollRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" }); };

  return (
    <section id="concepts" data-testid="concepts-section" className="py-14 sm:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between mb-10 sm:mb-14">
          <div>
            <p className="text-[12px] font-medium tracking-[0.2em] text-[#86868B] uppercase mb-3">Our Concepts</p>
            <h2 className="font-editorial text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-[#1D1D1F] leading-tight">Three concepts. <span className="italic">One ROCH.</span></h2>
          </div>
          <div className="flex gap-2 sm:hidden">
            <button onClick={() => scroll(-1)} disabled={!canLeft} className="w-9 h-9 rounded-full border border-[#E5E5E7] flex items-center justify-center text-[#6E6E73] disabled:opacity-30 transition-all"><ChevronDown size={16} className="rotate-90" /></button>
            <button onClick={() => scroll(1)} disabled={!canRight} className="w-9 h-9 rounded-full border border-[#E5E5E7] flex items-center justify-center text-[#6E6E73] disabled:opacity-30 transition-all"><ChevronDown size={16} className="-rotate-90" /></button>
          </div>
        </div>
        <div ref={scrollRef} onScroll={checkScroll} className="carousel-scroll lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible" data-testid="concepts-carousel">
          {data.map((c, i) => (
            <div key={i} className="carousel-item min-w-[240px] max-w-[80vw] sm:min-w-[340px] sm:max-w-none lg:min-w-0">
              <ConceptCard c={c} i={i} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== Photo Strip =====
function PhotoStrip() {
  const images = [
    { src: ASSETS.ramen, pos: "object-center" },
    { src: ASSETS.chefTable, pos: "object-center" },
    { src: ASSETS.cake, pos: "object-top" },
    { src: ASSETS.award, pos: "object-center" },
    { src: ASSETS.team, pos: "object-center" },
    { src: ASSETS.founder, pos: "object-top" },
  ];
  return (
    <section data-testid="photo-strip" className="py-3 overflow-hidden bg-[#FAFAFA]">
      <div className="photo-strip-track">
        {[...images, ...images].map((img, i) => (
          <div key={i} className="flex-shrink-0 w-48 sm:w-64 h-32 sm:h-44 rounded-xl overflow-hidden mx-2 grayscale hover:grayscale-0 transition-all duration-500 hover:scale-105">
            <img src={img.src} alt="" className={`w-full h-full object-cover ${img.pos}`} loading="lazy" />
          </div>
        ))}
      </div>
    </section>
  );
}

// ===== Recognition =====
function Recognition({ recognition }) {
  const award = recognition.find(r => r.type === "award");
  const press = recognition.filter(r => r.type === "press");
  const [ref, visible] = useReveal();

  return (
    <section ref={ref} data-testid="recognition-section" className="py-14 sm:py-20 bg-[#FAFAFA]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className={`text-center mb-10 sm:mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-[12px] font-medium tracking-[0.2em] text-[#86868B] uppercase mb-3">Recognition</p>
          <h2 className="font-editorial text-4xl sm:text-5xl font-bold text-[#1D1D1F] leading-tight">We're just <span className="italic">getting started.</span></h2>
        </div>

        {award && (
          <div className="mb-10 rounded-2xl overflow-hidden bg-white border border-[#E5E5E7] hover:shadow-lg transition-shadow" data-testid="award-feature">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="aspect-[4/3] lg:aspect-auto overflow-hidden bg-[#F5F5F7]">
                <img src={award.image_url || ASSETS.award} alt={award.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-8 sm:p-12 flex flex-col justify-center">
                <p className="text-[12px] font-semibold tracking-[0.2em] text-[#D97706] uppercase mb-4">Winner · {award.date}</p>
                <h3 className="font-editorial text-2xl sm:text-3xl font-bold text-[#1D1D1F] mb-1">{award.organisation}</h3>
                <p className="text-lg sm:text-xl font-editorial italic text-[#6E6E73] mb-6">{award.title}</p>
                <div className="text-[14px] text-[#86868B]"><p>Chef Rohit Chidurala</p><p>ROCH Chef's Table</p></div>
              </div>
            </div>
          </div>
        )}

        {press.length > 0 && (
          <>
            <p className="text-[11px] font-semibold tracking-[0.25em] text-[#AEAEB2] uppercase mb-6 text-center">As Featured In</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {press.map((p, i) => (
                <a key={i} href={p.external_url} target="_blank" rel="noopener noreferrer" data-testid={`press-item-${i}`}
                  className="group p-6 rounded-xl border border-[#E5E5E7] bg-white hover:border-[#D1D1D6] hover:shadow-sm transition-all">
                  <p className="text-[11px] font-bold tracking-[0.15em] text-[#D97706] uppercase mb-2">{p.organisation}</p>
                  <h4 className="font-editorial text-[17px] font-semibold text-[#1D1D1F] leading-snug mb-3">"{p.title}"</h4>
                  <span className="inline-flex items-center gap-1 text-[12px] text-[#86868B] group-hover:text-[#1D1D1F] transition-colors">Read <ExternalLink size={11} /></span>
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ===== Why ROCH =====
function WhyRoch() {
  const items = [
    { icon: "🏠", title: "Accommodation & Food", desc: "Stay and meals provided for the team" },
    { icon: "🍜", title: "Fine Dining Exposure", desc: "10-seat tasting menu environment" },
    { icon: "🔥", title: "Japanese Kitchen Techniques", desc: "Ramen broths, fermentation, Japanese prep" },
    { icon: "🎂", title: "High-End Cake Artistry", desc: "Premium buttercream and celebration cakes" },
    { icon: "🧪", title: "Menu R&D", desc: "Recipe development and testing" },
    { icon: "👨‍🍳", title: "Direct Founder Mentorship", desc: "Work alongside Chef Rohit daily" },
    { icon: "📋", title: "Structured Systems", desc: "SOPs, prep logs, systematic learning" },
    { icon: "🚀", title: "Leadership Growth", desc: "Grow into leadership as ROCH scales" },
    { icon: "🔄", title: "Cross-Concept Learning", desc: "Rotate across three culinary worlds" },
  ];
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);
  useEffect(() => { checkScroll(); }, [checkScroll]);
  const scroll = (dir) => { scrollRef.current?.scrollBy({ left: dir * 280, behavior: "smooth" }); };

  return (
    <section id="why-roch" data-testid="why-roch-section" className="py-14 sm:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[12px] font-medium tracking-[0.2em] text-[#86868B] uppercase mb-3">Why ROCH</p>
            <h2 className="font-editorial text-4xl sm:text-5xl font-bold text-[#1D1D1F] leading-tight">This is where <span className="italic">you grow fast.</span></h2>
          </div>
          <div className="hidden sm:flex gap-2">
            <button onClick={() => scroll(-1)} disabled={!canLeft} className="w-9 h-9 rounded-full border border-[#E5E5E7] flex items-center justify-center text-[#6E6E73] hover:bg-[#F5F5F7] disabled:opacity-30 transition-all" data-testid="why-roch-prev"><ChevronDown size={16} className="rotate-90" /></button>
            <button onClick={() => scroll(1)} disabled={!canRight} className="w-9 h-9 rounded-full border border-[#E5E5E7] flex items-center justify-center text-[#6E6E73] hover:bg-[#F5F5F7] disabled:opacity-30 transition-all" data-testid="why-roch-next"><ChevronDown size={16} className="-rotate-90" /></button>
          </div>
        </div>
        <div ref={scrollRef} onScroll={checkScroll} className="carousel-scroll" data-testid="why-roch-carousel">
          {items.map((b, i) => (
            <div key={i} className="carousel-item min-w-[240px] sm:min-w-[260px]" data-testid={`benefit-${i}`}>
              <div className="group p-5 h-full rounded-xl border border-[#E5E5E7] bg-[#FAFAFA] hover:bg-[#1D1D1F] hover:border-[#1D1D1F] transition-all duration-500 cursor-default">
                <span className="text-2xl block mb-3 group-hover:scale-110 transition-transform">{b.icon}</span>
                <h4 className="font-semibold text-[15px] text-[#1D1D1F] group-hover:text-white mb-1 transition-colors">{b.title}</h4>
                <p className="text-[13px] text-[#86868B] group-hover:text-[#AEAEB2] leading-relaxed transition-colors">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== Big Quote Divider =====
function QuoteDivider() {
  const [ref, visible] = useReveal();
  return (
    <section ref={ref} className="py-12 sm:py-16 bg-[#FAFAFA] overflow-hidden" data-testid="quote-divider">
      <div className={`max-w-4xl mx-auto px-5 sm:px-8 text-center transition-all duration-1000 ${visible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
        <p className="font-editorial text-[clamp(1.6rem,4vw,3rem)] font-bold text-[#1D1D1F] leading-snug italic">
          "We're not hiring to fill positions. <br className="hidden sm:block" />
          <span className="text-[#D97706]">We're building a founding team.</span>"
        </p>
        <p className="mt-4 text-[14px] text-[#86868B] font-medium">— Chef Rohit Chidurala, Founder</p>
      </div>
    </section>
  );
}

// ===== Open Positions =====
function OpenPositions({ roles, onApply }) {
  const [filter, setFilter] = useState("all");
  const [expandedRole, setExpandedRole] = useState(null);
  const filters = ["all", "roch_ramen", "roch_chefs_table", "roch_cake_studio", "central_team"];
  const filtered = filter === "all" ? roles : roles.filter(r => r.entity === filter);
  const [ref, visible] = useReveal();

  return (
    <section id="roles" ref={ref} data-testid="roles-section" className="py-14 sm:py-20 bg-[#FAFAFA]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className={`text-center mb-8 sm:mb-10 transition-all duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
          <p className="text-[12px] font-medium tracking-[0.2em] text-[#86868B] uppercase mb-3">Open Positions</p>
          <h2 className="font-editorial text-4xl sm:text-5xl font-bold text-[#1D1D1F]">Join the team.</h2>
          <p className="mt-3 text-[15px] text-[#86868B]">{roles.length} open position{roles.length !== 1 ? "s" : ""} across three concepts</p>
        </div>

        <div className="filter-scroll mb-8 justify-center" data-testid="entity-filters">
          {filters.map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-5 py-2 rounded-full text-[13px] font-medium whitespace-nowrap transition-all ${
                filter === f ? "bg-[#1D1D1F] text-white shadow-md" : "bg-white text-[#6E6E73] border border-[#E5E5E7] hover:border-[#D1D1D6] hover:text-[#1D1D1F]"
              }`}>
              {f === "all" ? "All" : ENTITY_SHORT[f]}
              {f !== "all" && <span className="ml-1 opacity-50">({roles.filter(r => r.entity === f).length})</span>}
            </button>
          ))}
        </div>

        <div className="space-y-2" data-testid="roles-list">
          {filtered.map(role => {
            const isOpen = expandedRole === role.id;
            const title = role.subtitle ? `${role.title} — ${role.subtitle}` : role.title;
            return (
              <div key={role.id} className="border border-[#E5E5E7] rounded-xl overflow-hidden bg-white hover:shadow-sm transition-shadow" data-testid={`role-card-${role.id}`}>
                <div className="p-5 cursor-pointer hover:bg-[#FAFAFA] transition-colors" onClick={() => setExpandedRole(isOpen ? null : role.id)}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="px-2.5 py-0.5 bg-[#F5F5F7] text-[11px] font-semibold text-[#6E6E73] rounded-full tracking-wide uppercase">{ENTITY_SHORT[role.entity] || role.entity}</span>
                        {role.featured && <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] text-[11px] font-medium rounded-full animate-pulse">Hiring</span>}
                      </div>
                      <h4 className="font-semibold text-[16px] text-[#1D1D1F]">{title}</h4>
                      <div className="flex items-center gap-3 mt-1 text-[13px] text-[#86868B]">
                        <span className="flex items-center gap-1"><MapPin size={12} />{role.location}</span>
                        <span className="flex items-center gap-1"><Clock size={12} />{role.employment_type || "Full Time"}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[14px] font-medium text-[#6E6E73]">₹{role.salary}</span>
                      <ChevronDown size={16} className={`text-[#AEAEB2] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </div>
                </div>
                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                  <div className="px-5 pb-5 border-t border-[#F0F0F2] pt-4">
                    {role.responsibilities?.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-[13px] font-semibold text-[#1D1D1F] mb-2">Responsibilities</h5>
                        <ul className="space-y-1">
                          {role.responsibilities.map((r, i) => <li key={i} className="text-[13px] text-[#6E6E73] flex items-start gap-2"><span className="mt-[7px] w-1 h-1 rounded-full bg-[#D97706] flex-shrink-0" />{r}</li>)}
                        </ul>
                      </div>
                    )}
                    <button onClick={() => onApply(role)} data-testid={`apply-role-${role.id}`}
                      className="group px-6 py-2.5 bg-[#1D1D1F] text-white font-medium text-[14px] rounded-full hover:bg-[#333] transition-colors inline-flex items-center gap-2">
                      Apply for this role <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-16 bg-white rounded-2xl border border-[#E5E5E7]">
              <p className="font-editorial text-xl text-[#AEAEB2] italic mb-2">No open positions right now.</p>
              <p className="text-[14px] text-[#D1D1D6]">Check back soon — we're always growing.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ===== Culture =====
function Culture() {
  const vals = ["Discipline", "Curiosity", "Consistency", "Ownership", "Attention to Detail", "Emotional Maturity", "Long-term Thinking"];
  const [ref, visible] = useReveal();
  return (
    <section ref={ref} data-testid="culture-section" className="py-14 sm:py-20 bg-white">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <p className="text-[12px] font-medium tracking-[0.2em] text-[#86868B] uppercase mb-3">Culture</p>
        <h2 className="font-editorial text-4xl sm:text-5xl font-bold text-[#1D1D1F] leading-tight mb-5">We take the <span className="italic">work seriously.</span></h2>
        <p className="text-[16px] text-[#6E6E73] leading-relaxed mb-6 max-w-lg mx-auto">ROCH is built for people who want to become exceptionally good at what they do.</p>
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {vals.map((v, i) => (
            <span key={v} className={`px-4 py-2 border border-[#E5E5E7] rounded-full text-[13px] font-medium text-[#6E6E73] hover:bg-[#1D1D1F] hover:text-white hover:border-[#1D1D1F] cursor-default transition-all duration-300 ${visible ? "opacity-100 scale-100" : "opacity-0 scale-90"}`} style={{ transitionDelay: `${i * 60}ms` }}>{v}</span>
          ))}
        </div>
        <p className="text-[14px] text-[#AEAEB2] max-w-md mx-auto">If you want responsibility, exposure and rapid learning, you'll feel at home here.</p>
      </div>
    </section>
  );
}

// ===== Founder =====
function Founder() {
  const creds = ["HYBIZ TV Food Awards 2026 — New Menu Innovation of the Year", "MasterChef India Telugu — Semi-Finalist", "International Michelin-Kitchen Experience", "Founder — ROCH"];
  const [ref, visible] = useReveal();
  return (
    <section ref={ref} data-testid="founder-section" className="py-14 sm:py-20 bg-[#FAFAFA]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className={`flex flex-col sm:flex-row items-center gap-8 max-w-3xl mx-auto transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="w-40 h-52 sm:w-48 sm:h-64 flex-shrink-0 rounded-2xl overflow-hidden bg-[#E5E5E7] group">
            <img src={ASSETS.founder} alt="Chef Rohit Chidurala" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
          <div className="text-center sm:text-left">
            <p className="text-[12px] font-medium tracking-[0.2em] text-[#86868B] uppercase mb-2">Mentorship</p>
            <h3 className="font-editorial text-[24px] font-bold text-[#1D1D1F] mb-0.5">Rohit Chidurala</h3>
            <p className="text-[14px] text-[#86868B] mb-4">Founder & Executive Chef</p>
            <p className="text-[15px] text-[#6E6E73] leading-relaxed mb-4">Work alongside Chef Rohit across menu development, service and kitchen systems. International kitchen experience and competitive cooking translated into everyday learning.</p>
            <div className="space-y-2">
              {creds.map((c, i) => <div key={i} className="flex items-start gap-2 text-[13px] text-[#6E6E73] sm:justify-start justify-center"><span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-[#D97706] flex-shrink-0" />{c}</div>)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== Story =====
function OurStory() {
  const events = [
    { yr: "2026", t: "ROCH Cake Studio", d: "Premium custom cake studio launches in Kondapur." },
    { yr: "2026", t: "ROCH Chef's Table", d: "10-seat progressive Telugu tasting menu opens." },
    { yr: "2026", t: "Industry Recognition", d: "HYBIZ TV Food Awards — New Menu Innovation of the Year." },
    { yr: "2026", t: "ROCH Ramen", d: "Japanese ramen concept begins, completing the three-concept vision." },
  ];
  const [ref, visible] = useReveal();
  return (
    <section id="story" ref={ref} data-testid="story-section" className="py-14 sm:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-10"><h2 className="font-editorial text-4xl sm:text-5xl font-bold text-[#1D1D1F]">The journey.</h2></div>
        <div className="max-w-xl mx-auto">
          {events.map((e, i) => (
            <div key={i} className={`flex gap-5 mb-6 last:mb-0 transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`} style={{ transitionDelay: `${i * 150}ms` }} data-testid={`timeline-${i}`}>
              <div className="flex flex-col items-center">
                <div className="w-3 h-3 rounded-full bg-[#D97706] mt-1 flex-shrink-0 shadow-[0_0_0_4px_rgba(217,119,6,0.15)]" />
                {i < events.length - 1 && <div className="w-px flex-1 bg-gradient-to-b from-[#D97706] to-[#E5E5E7] mt-2" />}
              </div>
              <div className="pb-2">
                <p className="text-[12px] font-semibold text-[#D97706] mb-0.5">{e.yr}</p>
                <h4 className="font-semibold text-[16px] text-[#1D1D1F] mb-0.5">{e.t}</h4>
                <p className="text-[14px] text-[#86868B]">{e.d}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-center mt-10 font-editorial text-2xl sm:text-3xl italic text-[#AEAEB2]">And we're just getting started.</p>
      </div>
    </section>
  );
}

// ===== Application Form =====
function ApplicationForm({ roles, prefillRole, onClose }) {
  const [form, setForm] = useState({
    full_name: "", whatsapp_number: "", email: "", role: prefillRole?.title || "",
    entity: prefillRole?.entity || "", department: prefillRole?.department || "",
    years_experience: "", current_city: "", current_company: "",
    available_joining_date: "", current_salary: "", expected_salary: "",
    portfolio_link: "", why_select_you: "",
  });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState(prefillRole ? 2 : 1);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const entRoles = form.entity ? roles.filter(r => r.entity === form.entity) : roles;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.whatsapp_number || !form.email || !form.role) { toast.error("Please fill required fields"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (cv) fd.append("cv", cv);
      await axios.post(`${API}/applications`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" data-testid="application-success" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl p-8 sm:p-10 max-w-sm w-full text-center shadow-xl animate-scale-in">
        <div className="w-12 h-12 bg-[#F5F5F7] rounded-full flex items-center justify-center mx-auto mb-5"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg></div>
        <h3 className="font-editorial text-2xl font-bold text-[#1D1D1F] mb-2">Application received.</h3>
        <p className="text-[14px] text-[#86868B] mb-6">Our team will review your profile and contact shortlisted candidates directly.</p>
        {form.entity && <p className="text-[13px] text-[#AEAEB2] mb-0.5">{ENTITY_FULL[form.entity] || form.entity}</p>}
        <p className="font-medium text-[15px] text-[#1D1D1F] mb-6">{form.role}</p>
        <a href="#roles" onClick={onClose} className="inline-block px-6 py-2.5 bg-[#1D1D1F] text-white font-medium rounded-full text-[14px]">Explore other roles</a>
      </div>
    </div>
  );

  const inputCls = "w-full px-4 py-3 border border-[#E5E5E7] rounded-xl bg-white text-[#1D1D1F] text-[15px] focus:outline-none focus:ring-2 focus:ring-[#1D1D1F]/10 focus:border-[#1D1D1F] transition-all placeholder:text-[#AEAEB2]";
  const labelCls = "block text-[13px] font-medium text-[#1D1D1F] mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="application-form-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl animate-slide-up">
        <div className="sticky top-0 bg-white px-6 pt-6 pb-4 border-b border-[#F0F0F2] z-10 flex items-center justify-between">
          <h3 className="font-editorial text-xl font-bold text-[#1D1D1F]">{prefillRole ? `Apply — ${prefillRole.title}` : "Apply to ROCH"}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F7] rounded-full transition-colors"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {!prefillRole && step === 1 && (
            <>
              <div><label className={labelCls}>Which concept interests you?</label>
                <select value={form.entity} onChange={e => { set("entity", e.target.value); set("role", ""); }} className={inputCls} data-testid="select-entity">
                  <option value="">Select concept...</option>
                  {Object.entries(ENTITY_FULL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  <option value="open">Open to Any</option>
                </select>
              </div>
              <div><label className={labelCls}>Role applying for</label>
                <select value={form.role} onChange={e => set("role", e.target.value)} className={inputCls} data-testid="select-role">
                  <option value="">Select role...</option>
                  {entRoles.map(r => <option key={r.id} value={r.subtitle ? `${r.title} - ${r.subtitle}` : r.title}>{r.subtitle ? `${r.title} - ${r.subtitle}` : r.title}</option>)}
                  <option value="General / Other">General / Other</option>
                </select>
              </div>
              <button type="button" onClick={() => form.role ? setStep(2) : toast.error("Please select a role")} className="w-full py-3 bg-[#1D1D1F] text-white font-medium rounded-full text-[15px] hover:bg-[#333] transition-colors">Continue</button>
            </>
          )}
          {step === 2 && (
            <>
              {!prefillRole && <div className="flex items-center gap-2 p-3 bg-[#F5F5F7] rounded-lg mb-1"><span className="text-[13px] text-[#86868B]">Applying for:</span><span className="text-[13px] font-semibold text-[#1D1D1F]">{form.role}</span><button type="button" onClick={() => setStep(1)} className="ml-auto text-[12px] text-[#D97706] font-medium">Change</button></div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><label className={labelCls}>Full Name *</label><input type="text" value={form.full_name} onChange={e => set("full_name", e.target.value)} className={inputCls} data-testid="input-name" placeholder="Your full name" required /></div>
                <div><label className={labelCls}>WhatsApp Number *</label><input type="tel" value={form.whatsapp_number} onChange={e => set("whatsapp_number", e.target.value)} className={inputCls} data-testid="input-whatsapp" placeholder="+91..." required /></div>
                <div><label className={labelCls}>Email *</label><input type="email" value={form.email} onChange={e => set("email", e.target.value)} className={inputCls} data-testid="input-email" placeholder="your@email.com" required /></div>
                <div><label className={labelCls}>Current City</label><input type="text" value={form.current_city} onChange={e => set("current_city", e.target.value)} className={inputCls} placeholder="Hyderabad" /></div>
                <div><label className={labelCls}>Years of Experience</label>
                  <select value={form.years_experience} onChange={e => set("years_experience", e.target.value)} className={inputCls} data-testid="select-experience">
                    <option value="">Select...</option><option value="Fresher">Fresher</option><option value="1-2 years">1-2 years</option><option value="3-5 years">3-5 years</option><option value="5-10 years">5-10 years</option><option value="10+ years">10+ years</option>
                  </select>
                </div>
                <div><label className={labelCls}>Current / Last Company</label><input type="text" value={form.current_company} onChange={e => set("current_company", e.target.value)} className={inputCls} placeholder="Company name" /></div>
                <div><label className={labelCls}>Available Joining Date</label><input type="date" value={form.available_joining_date} onChange={e => set("available_joining_date", e.target.value)} className={inputCls} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Upload CV</label>
                  <label className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-dashed border-[#D1D1D6] rounded-xl cursor-pointer hover:border-[#86868B] hover:bg-[#FAFAFA] transition-all" data-testid="cv-upload">
                    <Upload size={15} className="text-[#AEAEB2]" /><span className="text-[14px] text-[#AEAEB2]">{cv ? cv.name : "PDF, DOC, DOCX"}</span>
                    <input type="file" accept=".pdf,.doc,.docx" onChange={e => setCv(e.target.files[0])} className="hidden" />
                  </label>
                </div>
                <div className="sm:col-span-2"><label className={labelCls}>Portfolio Link</label><input type="url" value={form.portfolio_link} onChange={e => set("portfolio_link", e.target.value)} className={inputCls} placeholder="https://..." /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Why should we select you? *</label>
                  <textarea value={form.why_select_you} onChange={e => set("why_select_you", e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="input-why" placeholder="Tell us about yourself..." required />
                </div>
              </div>
              <button type="submit" disabled={submitting} data-testid="submit-application" className="w-full py-3.5 bg-[#1D1D1F] text-white font-medium rounded-full text-[15px] hover:bg-[#333] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting ? "Submitting..." : "Submit Application"} {!submitting && <Send size={15} />}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

// ===== Final CTA =====
function FinalCTA({ onApply, onExplore }) {
  const [ref, visible] = useReveal();
  return (
    <section ref={ref} data-testid="final-cta" className="py-16 sm:py-24 bg-[#1D1D1F] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='white' fill-opacity='1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3C/g%3E%3C/svg%3E\")" }} />
      <div className={`max-w-3xl mx-auto px-5 text-center relative z-10 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="font-editorial text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-white leading-tight mb-4">The next chapter <span className="italic text-[#D97706]">needs a team.</span></h2>
        <p className="text-[16px] text-[#86868B] mb-8">Three concepts. One growing team. And a lot left to build.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={onExplore} className="group px-7 py-3.5 bg-white text-[#1D1D1F] font-medium rounded-full text-[15px] hover:bg-[#F5F5F7] transition-colors inline-flex items-center gap-2 justify-center">See Open Roles <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></button>
          <button onClick={onApply} className="px-7 py-3.5 border border-[#52525B] text-white font-medium rounded-full text-[15px] hover:bg-white/10 transition-colors">General Application</button>
        </div>
      </div>
    </section>
  );
}

// ===== Footer =====
function Footer() {
  const socials = [
    { name: "ROCH Chef's Table", url: "https://www.instagram.com/rochchefstable" },
    { name: "ROCH Cake Studio", url: "https://www.instagram.com/rochcakestudio/" },
    { name: "Rohit Chidurala", url: "https://www.instagram.com/rohit_chidurala/" },
  ];
  return (
    <footer className="py-10 bg-white border-t border-[#E5E5E7]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <img src={ASSETS.logo} alt="ROCH" className="h-10 mb-3" loading="lazy" />
            <p className="text-[13px] text-[#86868B]">Three concepts. One ROCH.</p>
            <p className="text-[13px] text-[#AEAEB2] mt-1 flex items-center gap-1"><MapPin size={12} />Kondapur, Hyderabad</p>
          </div>
          <div>
            <h4 className="text-[13px] font-semibold text-[#1D1D1F] mb-3">Follow Us</h4>
            {socials.map((s, i) => <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[13px] text-[#86868B] hover:text-[#1D1D1F] transition-colors mb-2"><Instagram size={13} />{s.name}</a>)}
          </div>
          <div>
            <h4 className="text-[13px] font-semibold text-[#1D1D1F] mb-3">Quick Links</h4>
            <a href="#concepts" className="block text-[13px] text-[#86868B] hover:text-[#1D1D1F] mb-2">Our Concepts</a>
            <a href="#roles" className="block text-[13px] text-[#86868B] hover:text-[#1D1D1F] mb-2">Open Roles</a>
            <a href="#story" className="block text-[13px] text-[#86868B] hover:text-[#1D1D1F] mb-2">Our Story</a>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-[#F0F0F2] text-center text-[12px] text-[#AEAEB2]">&copy; {new Date().getFullYear()} ROCH Hospitality</div>
      </div>
    </footer>
  );
}

// ===== HomePage =====
function HomePage() {
  const [roles, setRoles] = useState([]);
  const [recognition, setRecognition] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [prefillRole, setPrefillRole] = useState(null);

  useEffect(() => {
    axios.get(`${API}/roles`).then(r => setRoles(r.data)).catch(() => {});
    axios.get(`${API}/recognition`).then(r => setRecognition(r.data)).catch(() => {});
  }, []);

  const openForm = (role = null) => { setPrefillRole(role); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setPrefillRole(null); };
  const scrollRoles = () => document.getElementById("roles")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-white" data-testid="homepage">
      <Toaster position="top-center" richColors />
      <Navbar onApply={() => openForm()} />
      <Hero onApply={() => openForm()} onExplore={scrollRoles} />
      <NumbersStrip />
      <Marquee items={["Ramen", "Fine Dining", "Cake Art", "Innovation", "Passion", "Technique", "Storytelling", "Craft"]} speed="35s" />
      <Concepts />
      <PhotoStrip />
      {recognition.length > 0 && <Recognition recognition={recognition} />}
      <WhyRoch />
      <QuoteDivider />
      <OpenPositions roles={roles} onApply={openForm} />
      <Culture />
      <Founder />
      <OurStory />
      <FinalCTA onApply={() => openForm()} onExplore={scrollRoles} />
      <Footer />
      <div className="sticky-apply sm:hidden"><button onClick={() => openForm()} data-testid="sticky-apply-btn" className="w-full py-3 bg-[#1D1D1F] text-white font-medium rounded-full text-[14px]">Apply Now</button></div>
      {showForm && <ApplicationForm roles={roles} prefillRole={prefillRole} onClose={closeForm} />}
    </div>
  );
}

// ===== Auth Callback =====
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/admin", { replace: true }); return; }
    window.history.replaceState(null, '', '/admin');
    const exchange = async () => {
      try {
        const resp = await axios.post(`${API}/auth/session`, { session_id: match[1] });
        if (resp.data.session_token) localStorage.setItem("roch_session_token", resp.data.session_token);
        navigate("/admin", { replace: true, state: { user: resp.data } });
      } catch { navigate("/admin", { replace: true }); }
    };
    exchange();
  }, [navigate]);
  return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="animate-spin w-6 h-6 border-2 border-[#D97706] border-t-transparent rounded-full" /></div>;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return <Routes><Route path="/" element={<HomePage />} /><Route path="/admin" element={<AdminPage />} /></Routes>;
};

function App() { return <BrowserRouter><AppRouter /></BrowserRouter>; }
export default App;
